import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Feedback Hub UI State
 *
 * Manages ONLY client-side UI state.
 * Server data is handled by TanStack Query hooks.
 *
 * This store contains:
 * - Tab navigation state (activeTab)
 * - Modal open/close state
 * - View preferences
 *
 * DO NOT store server data here (feedbacks, meetings, etc.)
 */
interface FeedbackUIStore {
  // ================================
  // TAB NAVIGATION
  // ================================

  /** Current active tab */
  activeTab: 'hub' | 'rapido' | 'completo' | 'guia' | 'agenda'

  /** Set active tab */
  setActiveTab: (tab: FeedbackUIStore['activeTab']) => void

  // ================================
  // MODAL STATE
  // ================================

  /** Agenda dialog open state */
  isAgendaDialogOpen: boolean

  /** Open agenda dialog */
  openAgendaDialog: () => void

  /** Close agenda dialog */
  closeAgendaDialog: () => void

  // ================================
  // FORM STATE (ephemeral)
  // ================================

  /** Selected employee ID for meeting form */
  selectedEmployeeId: string

  /** Set selected employee */
  setSelectedEmployeeId: (id: string) => void

  /** Reset form state */
  resetFormState: () => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state */
  reset: () => void
}

const initialState = {
  activeTab: 'hub' as const,
  isAgendaDialogOpen: false,
  selectedEmployeeId: '',
}

/**
 * Feedback UI Store
 *
 * Persisted to localStorage so tab preferences survive page refreshes.
 * Modal state is NOT persisted (resets on refresh).
 */
export const useFeedbackUIStore = create<FeedbackUIStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Tab navigation
      setActiveTab: (tab) => set({ activeTab: tab }),

      // Modal state
      openAgendaDialog: () => set({ isAgendaDialogOpen: true }),
      closeAgendaDialog: () => set({ isAgendaDialogOpen: false }),

      // Form state
      setSelectedEmployeeId: (id) => set({ selectedEmployeeId: id }),
      resetFormState: () => set({ selectedEmployeeId: '' }),

      // Utility
      reset: () => set(initialState),
    }),
    {
      name: 'feedback-ui-store', // localStorage key
      // Only persist tab (not modal/form state)
      partialize: (state) => ({
        activeTab: state.activeTab,
      }),
    }
  )
)
